import { z } from "zod";

/** Yes this is duplicate code, but its not a lot of it */
export type JsonObject = { [key: string]: Json };
type JsonArray = Json[];
type Json = string | number | boolean | null | JsonObject | JsonArray;

/**
 * Use this instead of JSON.parse(JSON.stringify(...)) to ensure that the parsed JSON is valid.
 */
const LiteralSchema = z.union([z.string(), z.number(), z.boolean(), z.null()]);
const JsonSchema: z.ZodType<Json> = z.lazy(() =>
  z.union([LiteralSchema, z.array(JsonSchema), z.record(z.string(), JsonSchema)]),
);

export type AllowedGeometry = GeoJSONPoint | GeoJSONLineString | GeoJSONPolygon | GeoJSONMultiPolygon;
type Point = number[];

export type GeoJSONPoint = {
  type: "Point";
  coordinates: Point;
};

export type GeoJSONLineString = {
  type: "LineString";
  coordinates: Point[];
};
export type GeoJSONPolygon = {
  type: "Polygon";
  coordinates: Point[][]; // Array of linear rings
};

export type GeoJSONMultiPolygon = {
  type: "MultiPolygon";
  coordinates: Point[][][]; // Array of polygons
};

const coordinateSchema = z.number().finite();
const pointSchema = z.array(coordinateSchema).min(2);

// Annotate via `satisfies`, not `: z.ZodType<GeoJSONPoint>` — the latter sets the schema's
// input type to `unknown` (Zod 4 defaults the Input param), which diverges from the output and
// makes the schema unusable in Inngest `eventType`, whose payload requires input === output.
export const GeoJSONPointSchema = z.object({
  type: z.literal("Point"),
  coordinates: pointSchema,
}) satisfies z.ZodType<GeoJSONPoint>;

const GeoJSONLineSchema: z.ZodType<GeoJSONLineString> = z.object({
  type: z.literal("LineString"),
  coordinates: z.array(pointSchema),
});

export const GeoJSONPolygonSchema: z.ZodType<GeoJSONPolygon> = z.object({
  type: z.literal("Polygon"),
  coordinates: z.array(z.array(pointSchema)),
});

export const GeoJSONMultiPolygonSchema: z.ZodType<GeoJSONMultiPolygon> = z.object({
  type: z.literal("MultiPolygon"),
  coordinates: z.array(z.array(z.array(pointSchema))),
});

export const GeoJsonGeometriesSchema = z.union([
  GeoJSONPointSchema,
  GeoJSONLineSchema,
  GeoJSONPolygonSchema,
  GeoJSONMultiPolygonSchema,
]);

export const GeoJSONFeatureSchema: z.ZodType<GeoJSONFeature> = z.object({
  id: z.string(),
  type: z.literal("Feature"),
  properties: z.record(z.string(), JsonSchema),
  geometry: GeoJsonGeometriesSchema,
});

export type GeoJSONFeature<
  G extends AllowedGeometry | GeoJSONGeometryCollection = AllowedGeometry,
  P extends JsonObject = JsonObject,
> = {
  id: string;
  type: "Feature";
  properties: P;
  geometry: G;
};

export type GeoJSONFeatureCollection<
  G extends AllowedGeometry | GeoJSONGeometryCollection = AllowedGeometry,
  P extends JsonObject = JsonObject,
> = {
  type: "FeatureCollection";
  features: GeoJSONFeature<G, P>[];
};

export type GeoJSONGeometry = AllowedGeometry | GeoJSONGeometryCollection;

export type GeoJSONGeometryCollection = {
  type: "GeometryCollection";
  geometries: AllowedGeometry[];
};

export const GeoJSONFeatureCollectionSchema: z.ZodType<GeoJSONFeatureCollection> = z.object({
  type: z.literal("FeatureCollection"),
  features: GeoJSONFeatureSchema.array(),
});

type FeatureProps = { id: string; name: string; geographicAreaId: string } & JsonObject;

const PropertiesSchema = z
  .object({
    id: z.string(),
    name: z.string(),
    geographicAreaId: z.string(),
  })
  .catchall(JsonSchema);

export type PolygonAndMultiPolygonFeature = GeoJSONFeature<GeoJSONPolygon | GeoJSONMultiPolygon, FeatureProps>;

export const PolygonAndMultiPolygonFeatureSchema: z.ZodType<PolygonAndMultiPolygonFeature> = z.object({
  id: z.string(),
  type: z.literal("Feature"),
  properties: PropertiesSchema,
  geometry: z.union([GeoJSONPolygonSchema, GeoJSONMultiPolygonSchema]),
});

export type PolygonAndMultiPolygonFeatureCollection = GeoJSONFeatureCollection<
  GeoJSONPolygon | GeoJSONMultiPolygon,
  FeatureProps
>;

const geoPointCoordinatesSchema = z.tuple([z.number(), z.number()]);

export function geoPointToCoordinates(geoPoint: GeoJSONPoint): { longitude: number; latitude: number } | undefined {
  const rawCoordinates: unknown = geoPoint.coordinates;
  const result = geoPointCoordinatesSchema.safeParse(rawCoordinates);
  if (!result.success) {
    return undefined;
  }
  return { longitude: result.data[0], latitude: result.data[1] };
}

const COORDINATE_LABEL_PRECISION = 6;

/**
 * Human-readable "lat, lng" label for a point — used where a coordinate stands in for an address
 * (e.g. coordinate-entry quotes with no street address). Returns "Unknown location" for a
 * null/malformed point.
 */
export function formatGeoPointLabel(geoPoint: GeoJSONPoint | null | undefined): string {
  const coordinates = geoPoint ? geoPointToCoordinates(geoPoint) : undefined;
  if (!coordinates) {
    return "Unknown location";
  }
  return `${coordinates.latitude.toFixed(COORDINATE_LABEL_PRECISION)}, ${coordinates.longitude.toFixed(COORDINATE_LABEL_PRECISION)}`;
}
